import { INITIALIZATION_FAILED, INITIALIZING } from './types';
import { isMultienum } from 'customer-data-objects/property/PropertyIdentifier';
import { parseMultiEnumValue } from 'customer-data-property-utils/parseMultiEnumValue';
import { isCustomExternalOptionsProperty } from '../resolvers/isCustomExternalOptionsProperty';
import { isExternalOptionsProperty } from 'reference-resolvers-lite/utils/isExternalOptionsProperty';
import { INVALID, PENDING, VALID } from '../validation/PropertyValidationStatus';
import { makeConditionalPropertyOptionsRuleKey } from './useConditionalPropertyOptionsRules';

const isConditionalPropertyOptionsRuleActive = ({
  objectTypeId,
  getFormValue,
  propertyDefinitions,
  rule
}) => {
  if (objectTypeId !== rule.objectTypeId) {
    return false;
  }

  return rule.controllingPropertyValues.every(({
    propertyName,
    value
  }) => {
    const currentValue = getFormValue(propertyName);
    const propertyDefinition = propertyDefinitions[propertyName];

    if (isMultienum(propertyDefinition)) {
      const values = parseMultiEnumValue(value);
      const currentValues = parseMultiEnumValue(currentValue);
      return values.every(_ => currentValues.includes(_));
    }

    return value === currentValue;
  });
};

const isConditionalPropertyRuleActive = ({
  objectTypeId,
  getFormValue,
  propertyDefinitions,
  rule
}) => {
  if (objectTypeId !== rule.objectTypeId) {
    return false;
  }

  const {
    fieldName,
    fieldValue: ruleValue
  } = rule.controllingField;
  const currentValue = getFormValue(fieldName);
  const propertyDefinition = propertyDefinitions[fieldName];

  if (isMultienum(propertyDefinition)) {
    const values = parseMultiEnumValue(ruleValue);
    const currentValues = parseMultiEnumValue(currentValue);
    return values.every(_ => currentValues.includes(_));
  }

  return ruleValue === currentValue;
};

export const getConditionalPropertyDependencies = (objectTypeId, getFormValue, propertyDefinitions, conditionalPropertyRules) => {
  const activeRules = {};

  for (const rule of conditionalPropertyRules) {
    const active = isConditionalPropertyRuleActive({
      rule,
      objectTypeId,
      getFormValue,
      propertyDefinitions
    });

    if (active) {
      const controllingFieldName = rule.controllingField.fieldName;
      activeRules[controllingFieldName] = (Object.prototype.hasOwnProperty.call(activeRules, controllingFieldName) ? activeRules[controllingFieldName] : []).concat([...rule.dependentFields].sort((a, b) => {
        return a.displayOrder === b.displayOrder ? a.fieldName.localeCompare(b.fieldName) : a.displayOrder - b.displayOrder;
      }));
    }
  }

  return activeRules;
};

const mergePropertyDefinitionOptions = (propertyDefinitions, propertyDefinition1, propertyDefinition2) => {
  const originalPropertyDefinition = propertyDefinitions[propertyDefinition1.name];
  const optionValuesSet = new Set([...propertyDefinition1.options.map(option => option.value), ...propertyDefinition2.options.map(option => option.value)]);
  const filteredOptions = originalPropertyDefinition.options.filter(option => optionValuesSet.has(option.value));
  return Object.assign({}, propertyDefinition1, {
    options: filteredOptions
  });
};
/**
 * This function takes a dictionary as below and current values:
 * {
 *   0-1-cp1-v1-dp1 = {...dp1_definitions, options:[1,2,3]},
 *   0-1-cp1-v2-dp1 = {...dp1_definitions, options:[4,5,6]},
 * }
 * If the current value of cp1 is v1, this function returns
 * {
 *   dp1 : {...dp1_definitions, options:[1,2,3]}
 * }
 * Properties form then uses this definition instead of the original definition for dp1
 */


export const getPropertyDefinitionsWithFilteredOptionsActive = (objectTypeId, conditionalPropertyOptionsRules, getFormValue, propertyDefinitions, propertyDefinitionsWithFilteredOptions) => {
  const propertyDefinitionsWithFilteredOptionsActive = Object.create(null);

  for (const rule of conditionalPropertyOptionsRules) {
    if (isConditionalPropertyOptionsRuleActive({
      objectTypeId,
      getFormValue,
      propertyDefinitions,
      rule
    })) {
      const conditionalPropertyOptionsRuleKey = makeConditionalPropertyOptionsRuleKey({
        objectTypeId,
        rule
      });
      const propertyDefinition = propertyDefinitionsWithFilteredOptions.get(conditionalPropertyOptionsRuleKey);
      const existingPropertyDefinition = propertyDefinitionsWithFilteredOptionsActive[rule.dependentPropertyName]; // When there are multiple active rules, we need to merge the unique options

      if (existingPropertyDefinition) {
        propertyDefinitionsWithFilteredOptionsActive[rule.dependentPropertyName] = mergePropertyDefinitionOptions(propertyDefinitions, existingPropertyDefinition, propertyDefinition);
      } else {
        propertyDefinitionsWithFilteredOptionsActive[rule.dependentPropertyName] = propertyDefinition;
      }
    }
  }

  return propertyDefinitionsWithFilteredOptionsActive;
};
/** Flattens and returns a pre-order traversal of the graph */

export const getAllFormInputs = initialInputs => {
  function _getDependents(inputs, input) {
    inputs.push(input);
    return input.dependents.reduce(_getDependents, inputs);
  }

  return initialInputs.reduce(_getDependents, []);
};
/** Flattens and returns a pre-order traversal of the graph */

export const getAllFormProperties = initialProperties => {
  function _getDependents(properties, property) {
    properties.push(property);
    return property.dependents.reduce(_getDependents, properties);
  }

  return initialProperties.reduce(_getDependents, []);
};
/**
 * Flattens and returns a pre-order traversal of the graph, omitting
 * the initial properties (the first layer). Only dependent properties
 * (and their dependents, etc.) are returned.
 */

export const getDependentFormProperties = initialProperties => {
  function _getDependents(properties, property) {
    properties.push(property);
    return property.dependents.reduce(_getDependents, properties);
  }

  return initialProperties.reduce((properties, property) => property.dependents.reduce(_getDependents, properties), []);
};
export const getFormPropertyNames = properties => {
  return getAllFormProperties(properties).reduce((acc, property) => {
    acc.add(property.propertyName);
    return acc;
  }, new Set());
};
/** returns additional values in first set */

export function difference(a, b) {
  return [...a].filter(x => !b.has(x));
}
const emptyFormPropertiesDifference = {
  added: [],
  removed: []
};
export const getFormPropertiesDifference = (prev, next) => {
  if (prev === next) {
    return emptyFormPropertiesDifference;
  }

  const prevPropertyNames = getFormPropertyNames(prev);
  const nextPropertyNames = getFormPropertyNames(next);
  return {
    added: difference(nextPropertyNames, prevPropertyNames),
    removed: difference(prevPropertyNames, nextPropertyNames)
  };
};
/** Deduplicates inputs by property name. All inputs for same property have same value */

export const getUniqueFormInputs = inputs => {
  const seen = new Set();
  const uniqueInputs = getAllFormInputs(inputs).reduce((acc, input) => {
    if (!seen.has(input.property.name)) {
      seen.add(input.property.name);
      acc.push(input);
    }

    return acc;
  }, []);
  return uniqueInputs;
};
export const toPropertyWithDependents = property => {
  return Object.assign({}, property, {
    isRequired: Boolean(property.isRequired),
    dependents: [],
    parent: undefined
  });
};
/**
 * Builds the structure for showing nested properties within the form.
 * The output is a tree where the following conditions hold:
 *   - Each initial property in the form is the root of a branch that contains
 *     a node for each dependent property. This is recursive, but duplicates
 *     are pruned per-branch to prevent cycles.
 *   - Conditional rules are applied to each property in the tree that has
 *     been interacted with in the form (changedProperties)
 *   - If a dependent property would appear twice, prefer the closest to root
 *
 * General strategy to build:
 *   - Level-order traversal of the dependency tree
 *   - Treat each initial form property as their own branch and keep
 *     set of visited nodes per branch. Evaluate rules of already seen nodes
 *     once, but do not add them to queue.
 *   - Keep track of any conditional rules in a global set while building.
 *     Apply rules as a single pass through tree after all nodes evaluated.
 */

export const applyConditionalPropertyRules = (properties, dependencies, changedProperties, alwaysShowConditionalProperties) => {
  const queues = [];
  const visited = [];
  const requiredProperties = new Set();
  const initialProperties = [];

  for (const property of properties) {
    const node = toPropertyWithDependents(property);
    queues.push([node]);
    initialProperties.push(node);
    visited.push(new Set());
  }

  while (queues.some(branch => branch.length > 0)) {
    for (let branchIndex = 0; branchIndex < queues.length; branchIndex++) {
      const level = queues[branchIndex];
      const branchVisited = visited[branchIndex];
      const nextLevel = [];

      for (const property of level) {
        const propertyName = property.propertyName;

        if (property.isRequired) {
          requiredProperties.add(propertyName);
        }

        if (branchVisited.has(propertyName)) {
          continue;
        }

        if (property.parent) {
          property.parent.dependents.push(property);
        }

        if (alwaysShowConditionalProperties || changedProperties.has(propertyName)) {
          const dependents = (dependencies[propertyName] || []).map(dependent => ({
            propertyName: dependent.fieldName,
            isRequired: dependent.required,
            dependents: [],
            parent: property
          }));
          nextLevel.push(...dependents);
        }

        branchVisited.add(propertyName);
      }

      queues[branchIndex] = nextLevel;
    }
  }

  const markRequired = node => {
    node.isRequired = requiredProperties.has(node.propertyName);
    node.dependents.forEach(markRequired);
  };

  initialProperties.forEach(markRequired);
  return initialProperties;
};
export const buildPropertiesFormInitializingState = () => ({
  validationStatus: INITIALIZING,
  initializing: true,
  initializationFailed: false,
  loading: false,
  invalid: false,
  saveable: false,
  errors: new Map()
});
export const buildPropertiesFormInitializationFailedState = () => ({
  validationStatus: INITIALIZATION_FAILED,
  initializing: false,
  initializationFailed: true,
  loading: false,
  invalid: false,
  saveable: false,
  errors: new Map()
});
export const buildPropertiesFormPendingState = () => ({
  validationStatus: PENDING,
  initializing: false,
  initializationFailed: false,
  loading: true,
  invalid: false,
  saveable: false,
  errors: new Map()
});
export const buildPropertiesFormValidState = () => ({
  validationStatus: VALID,
  initializing: false,
  initializationFailed: false,
  loading: false,
  invalid: false,
  saveable: true,
  errors: new Map()
});
export const buildPropertiesFormInvalidState = errors => ({
  validationStatus: INVALID,
  initializing: false,
  initializationFailed: false,
  loading: false,
  invalid: true,
  saveable: false,
  errors
});
export const isValidEnumOption = (objectTypeId, propertyDefinition, value) => {
  if (!value || isCustomExternalOptionsProperty(propertyDefinition, objectTypeId) || isExternalOptionsProperty(propertyDefinition, objectTypeId)) {
    return true;
  }

  if (isMultienum(propertyDefinition)) {
    const optionValues = propertyDefinition.options.reduce((acc, option) => {
      acc.add(option.value);
      return acc;
    }, new Set());
    const values = parseMultiEnumValue(value);
    return values.every(_ => optionValues.has(_));
  }

  return propertyDefinition.options.some(option => option.value === value);
};
export const getMultiEnumPropertiesWithValue = ({
  propertyNames,
  propertyDefinitions,
  getFormValue
}) => [...propertyNames].filter(propertyName => propertyDefinitions[propertyName] && isMultienum(propertyDefinitions[propertyName]) && getFormValue(propertyName));
export const allValuesHaveRules = ({
  values,
  propertyName,
  conditionalPropertyOptionsRules
}) => values.every(value => conditionalPropertyOptionsRules.some(rule => rule.controllingPropertyValues[0].propertyName === propertyName && rule.controllingPropertyValues[0].value === value));